import { AxiosError } from 'axios';
import useForm from 'antd/es/form/hooks/useForm';
import { CommonFormProps, FormModeDependedTexts, JournalId } from 'types';
import { AppForm, AppFormProps, JournalChildPicker } from 'components/organisms';
import {
    DatePickerElement,
    DescriptionInputElement,
    FormNavButtons,
    NameInputElement,
    SendConsentToParentsInputElement,
} from 'components/molecules';
import { TripFormFields } from 'types/forms/trip';
import { commonTexts, journalTexts } from 'consts/text';
import { formFields } from 'consts/form/formFields';
import { parseDayjsDateIntoString } from 'utils/parsers/dateTime/parseDayjsDateIntoString';
import { useDependentFormFields } from 'hooks/useDependentFormFields/useDependentFormFields';

const addModeTexts: FormModeDependedTexts = {
    name: 'tripAdd',
    title: journalTexts.journalDetails.groupJournal.events.trips.addTrip,
    buttonText: commonTexts.actionLabels.add,
};

const editModeTexts: FormModeDependedTexts = {
    name: 'tripEdit',
    title: journalTexts.journalDetails.groupJournal.events.trips.form.editTrip,
    buttonText: commonTexts.actionLabels.save,
};

type TripAddEditFormProps = Omit<
    AppFormProps<TripFormFields>,
    'width' | 'title' | 'name' | 'onFinish' | 'error' | 'withGoBack'
> &
    Pick<CommonFormProps, 'mode'> & {
        journalId: JournalId;
        axiosError?: AxiosError | null;
        isLoading?: boolean;
        onFinish: (tripData: TripFormFields) => void;
    };

export const TripAddEditForm = ({
    mode,
    form,
    initialValues,
    journalId,
    axiosError,
    ...props
}: TripAddEditFormProps) => {
    const [formInstance] = useForm<TripFormFields>(form);
    const {
        watchedFieldsValues: watchedDateValue,
        handleWatchedFieldsValuesChange: handleWatchedDateValueChange,
    } = useDependentFormFields({
        watchedFields: [formFields.date],
        dependentFields: [formFields.childIds],
        formInstance,
    });

    const isAddMode = mode === 'add';
    const { name, title, buttonText, buttonIcon } = isAddMode ? addModeTexts : editModeTexts;
    const { date: dateValue } = watchedDateValue;
    const parsedDate = dateValue ? parseDayjsDateIntoString(dateValue, 'YYYY-MM-DD') : undefined;

    const tripAddEditError = axiosError ? commonTexts.errorMessages.unknown : '';

    return (
        <AppForm<TripFormFields>
            form={formInstance}
            name={name}
            title={title}
            error={tripAddEditError}
            withGoBack
            maxWidth={440}
            {...props}
        >
            <NameInputElement
                label={journalTexts.journalDetails.groupJournal.events.trips.form.tripName}
            />
            <DatePickerElement isHalfParentWidth onChange={handleWatchedDateValueChange} />
            <DescriptionInputElement
                label={journalTexts.journalDetails.groupJournal.events.trips.form.tripDescription}
            />
            <JournalChildPicker
                journalId={journalId}
                label={journalTexts.journalDetails.groupJournal.events.trips.form.attendanceList}
                dateFrom={parsedDate}
                dateTo={parsedDate}
                multiple
                optional
                disabled={!parsedDate}
            />
            <SendConsentToParentsInputElement />
            <FormNavButtons
                formValuesBeforeChanges={initialValues}
                buttonText={buttonText}
                leftIcon={buttonIcon}
                withPlus={isAddMode}
                withDeviceFloppy={!isAddMode}
            />
        </AppForm>
    );
};
